import React from 'react'
import useTranslation from 'next-translate/useTranslation'
import { Social } from '@/types/auth'
import LoginCandidateForm, {
  LoginCandidateFormProps,
} from '@/components/molecules/LoginCandidateForm'
import SocialIcon from '../atoms/SocialIcons/SocialIcon'

const SUPPORT_PROVIDERS = ['linkedin', 'facebook', 'github'] as const

const buttonClassName =
  'w-full border text-xl leading-none border-gray-200 transition-colors hover:border-black rounded-full flex items-center justify-center text-center py-4'

interface IProps {
  data: Social[]
  title?: string | React.ReactNode
  header?: boolean
  CredentialForm?: ({
    withResetPassword,
    withForgotPassword,
  }: LoginCandidateFormProps) => JSX.Element
}

interface SocialAuthButtonProps {
  social: Social
  labeled?: boolean
}

const openSocialPopup = (provider: string) => {
  const popupWindow = window.open(
    `/auth/by-${provider}`,
    'login-window',
    'left=100,top=100,width=600,height=600,dependent=yes,resizable=yes,scrollbars=yes'
  )
  popupWindow?.focus()
}

const SocialAuthButton = ({
  social,
  labeled = false,
}: SocialAuthButtonProps) => (
  <a
    href='#'
    data-provider={social.provider}
    onClick={(e) => {
      e.preventDefault()
      openSocialPopup(social.provider)
    }}
    title={social.title}
    aria-label={social.title}
    className={`${buttonClassName} ${labeled ? 'gap-4' : ''} ${
      social.provider
    }`}
  >
    <span
      aria-hidden='true'
      className='inline-flex items-center'
      style={social.provider === 'google' ? undefined : { color: social.color }}
    >
      <SocialIcon
        provider={social.provider}
        className='block'
        width={labeled ? 18 : 22}
      />
    </span>
    {labeled && (
      <span className='inline-flex items-center leading-none whitespace-nowrap'>
        {social.title}
      </span>
    )}
  </a>
)

const AuthContent = ({
  data,
  title,
  header = true,
  CredentialForm = LoginCandidateForm,
}: IProps) => {
  const { t } = useTranslation('common')
  const google = data.find((social) => social.provider === 'google')
  const supportSocials = SUPPORT_PROVIDERS.map((provider) =>
    data.find((social) => social.provider === provider)
  ).filter((social): social is Social => Boolean(social))

  return (
    <>
      {header && (
        <header className='mb-10'>
          <h1 className='text-3xl font-medium'>
            <span className='mr-2'>{title}</span>
          </h1>
        </header>
      )}
      <div className='flex flex-col gap-3'>
        {google && (
          <div className={google.provider}>
            <SocialAuthButton social={google} labeled />
          </div>
        )}
        {supportSocials.length > 0 && (
          <ul className='grid grid-cols-3 gap-3'>
            {supportSocials.map((social) => (
              <li className={social.provider} key={social.provider}>
                <SocialAuthButton social={social} />
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className='relative flex py-3 items-center'>
        <div className='flex-grow border-t border-gray-100' />
        <span className='flex-shrink mx-4'>{t('common:or')}</span>
        <div className='flex-grow border-t border-gray-100' />
      </div>
      <CredentialForm />
    </>
  )
}

export default AuthContent
